import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTenant } from '../contexts/TenantContext';
import api from '../api/axios';
import {
  Check, X, CreditCard, Shield, Sparkles, Crown, Zap, Star,
  AlertTriangle, CheckCircle, XCircle, Loader2, Building2, Lock
} from 'lucide-react';
import Icon3D from '../components/Icon3D';
import { parsePlan, getBaseTier } from '../lib/utils';

// ─── Plan definitions ────────────────────────────────────────────────────────

const TIERS = [
  {
    id: 'explorer',
    name: 'Explorer',
    questionBank: '1,250 Qs',
    dailyLimit: '200/day',
    icon: Zap,
    iconColour: 'amber',
    color: 'blue',
    prices: { monthly: 9.95, quarterly: 26, annual: 99 },
    savings: { quarterly: '13% off', annual: '17% off' },
    features: [
      { text: '1,250 questions bank', included: true },
      { text: '200 questions per day', included: true },
      { text: 'Diagnostic tests (1/day)', included: true },
      { text: 'Adaptive learning engine', included: true },
      { text: 'Full analytics & exam mode', included: true },
      { text: 'Test dates & past results', included: false },
      { text: 'Upload your own MCQs', included: false },
      { text: 'AI study plan for your test', included: false },
      { text: 'AI question generation', included: false },
    ],
  },
  {
    id: 'essential',
    name: 'Essential',
    questionBank: '5,000 Qs',
    dailyLimit: '500/day',
    icon: Star,
    iconColour: 'purple',
    popular: true,
    color: 'cyan',
    prices: { monthly: 19.95, quarterly: 42, annual: 149 },
    savings: { quarterly: '30% off', annual: '38% off' },
    features: [
      { text: '5,000 questions bank', included: true },
      { text: '500 questions per day', included: true },
      { text: 'Unlimited diagnostic tests', included: true },
      { text: 'Adaptive learning engine', included: true },
      { text: 'Full analytics & exam mode', included: true },
      { text: 'Test dates & past results', included: true },
      { text: 'Upload your own MCQs', included: true },
      { text: 'AI study plan for your test', included: false },
      { text: 'AI question generation', included: false },
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    questionBank: '15,000 Qs',
    dailyLimit: '1,000/day',
    icon: Sparkles,
    iconColour: 'indigo',
    color: 'violet',
    prices: { monthly: 29.95, quarterly: 69, annual: 249 },
    savings: { quarterly: '23% off', annual: '31% off' },
    features: [
      { text: '15,000 questions bank', included: true },
      { text: '1,000 questions per day', included: true },
      { text: 'Unlimited diagnostic tests', included: true },
      { text: 'Adaptive learning engine', included: true },
      { text: 'Full analytics & exam mode', included: true },
      { text: 'Test dates & past results', included: true },
      { text: 'Upload your own MCQs', included: true },
      { text: 'AI study plan for your test', included: true, highlight: true },
      { text: 'AI question generation', included: true, highlight: true },
    ],
  },
  {
    id: 'elite',
    name: 'Elite',
    questionBank: '95,000+ Qs',
    dailyLimit: 'Unlimited',
    icon: Crown,
    iconColour: 'orange',
    bestValue: true,
    color: 'amber',
    prices: { monthly: 39.95, quarterly: 105, annual: 349 },
    savings: { quarterly: '12% off', annual: '27% off' },
    features: [
      { text: '95,000+ questions — full bank', included: true },
      { text: 'Unlimited questions per day', included: true },
      { text: 'Unlimited diagnostic tests', included: true },
      { text: 'Adaptive learning engine', included: true },
      { text: 'Full analytics & exam mode', included: true },
      { text: 'Test dates & past results', included: true },
      { text: 'Upload your own MCQs', included: true },
      { text: 'AI study plan for your test', included: true, highlight: true },
      { text: 'AI question generation', included: true, highlight: true },
      { text: 'AI scaffolding — auto-targets your weak spots', included: true, highlight: true },
    ],
  },
];

const PERIOD_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
};

// ─── Colour helpers ──────────────────────────────────────────────────────────

const COLOR_MAP = {
  blue:   { bg: 'bg-slate-100', text: 'text-slate-500', button: '#64748b' },
  cyan:   { bg: 'bg-cyan-100',  text: 'text-cyan-600',  button: null }, // uses gradient
  violet: { bg: 'bg-teal-100',  text: 'text-teal-600',  button: '#0d9488' },
  amber:  { bg: 'bg-slate-100', text: 'text-slate-500', button: '#0e7490' },
};

// ─── Component ───────────────────────────────────────────────────────────────

const SubscriptionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const { tenant } = useTenant();
  const tenantName = tenant?.name || 'MedMCQ';
  const showUpgradeBanner = location.state?.upgrade;

  useEffect(() => {
    document.title = `Subscription | ${tenantName}`;
  }, [tenantName]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    if (sessionId && status === 'success') pollPaymentStatus(sessionId);
    else if (status === 'cancelled') setPaymentStatus({ type: 'cancelled', message: 'Payment was cancelled. You can try again when ready.' });
    loadSubscription();
  }, [searchParams]);

  const loadSubscription = async () => {
    try {
      const res = await api.get('/user/subscription');
      setSubscription(res.data);
    } catch (e) {
      console.error('Error loading subscription:', e);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      setPaymentStatus({ type: 'error', message: 'Payment status check timed out. Please check your email for confirmation.' });
      return;
    }
    try {
      const res = await api.get(`/payments/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setPaymentStatus({ type: 'success', message: 'Payment successful! Your subscription is now active.' });
        loadSubscription();
        return;
      }
      setPaymentStatus({ type: 'pending', message: 'Processing payment...' });
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      setPaymentStatus({ type: 'error', message: 'Error checking payment status.' });
    }
  };

  const handleSubscribe = async (tierId) => {
    setLoading(true);
    setProcessingPlan(tierId);
    try {
      const planId = `${tierId}_${billingPeriod}`;
      const res = await api.post(`/payments/create-checkout?package_id=${planId}`);
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
      else throw new Error('No checkout URL received');
    } catch (e) {
      alert(e.response?.data?.detail || 'Payment system is currently unavailable. Please try again later.');
      setLoading(false);
      setProcessingPlan(null);
    }
  };

  const currentPlanBase = getBaseTier(subscription?.subscription_plan);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-2 sm:px-4">

        {/* Payment status banner */}
        {paymentStatus && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            paymentStatus.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' :
            paymentStatus.type === 'error'   ? 'bg-red-50 border border-red-200 text-red-700' :
            paymentStatus.type === 'cancelled' ? 'bg-amber-50 border border-amber-200 text-amber-700' :
            'bg-blue-50 border border-blue-200 text-blue-700'
          }`}>
            {paymentStatus.type === 'success'   && <CheckCircle className="w-5 h-5" />}
            {paymentStatus.type === 'error'     && <XCircle className="w-5 h-5" />}
            {paymentStatus.type === 'pending'   && <Loader2 className="w-5 h-5 animate-spin" />}
            {paymentStatus.type === 'cancelled' && <AlertTriangle className="w-5 h-5" />}
            <span className="font-medium">{paymentStatus.message}</span>
          </div>
        )}

        {/* Active subscription banner */}
        {subscription && (subscription.subscription_status === 'active' || subscription.subscription_status === 'free_grant') && (
          <div className="mb-4 rounded-2xl p-5 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgb(6,182,212) 0%, rgb(8,145,178) 100%)' }}>
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center gap-4">
                <Icon3D icon={Crown} colour="orange" size="md" />
                <div>
                  <h3 className="text-xl font-bold">Active Subscription</h3>
                  <p className="text-cyan-100">
                    {parsePlan(subscription.subscription_plan).display} Plan
                    {subscription.subscription_status === 'free_grant' && ' (Free Grant)'}
                  </p>
                </div>
              </div>
              {subscription.subscription_end && (
                <div className="text-right">
                  <p className="text-sm text-cyan-100">Expires</p>
                  <p className="font-bold">{new Date(subscription.subscription_end).toLocaleDateString('en-GB')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upgrade banner (shown when redirected from a locked page) */}
        {showUpgradeBanner && (
          <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 flex items-center gap-3">
            <Lock className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">
              Upgrade your plan to unlock full access to all features, diagnostics, analytics, and more.
            </span>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">Choose Your Plan</h1>
          <p className="text-base sm:text-lg text-slate-500 dark:text-slate-400">Unlock your full learning potential with {tenantName}</p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">All prices in AUD · Secure payment via Stripe</p>
        </div>

        {/* Billing period toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-2xl p-1.5 gap-1" style={{ background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)' }}>
            {Object.entries(PERIOD_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setBillingPeriod(key)}
                className={`px-3 sm:px-6 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                  billingPeriod === key
                    ? 'text-white'
                    : 'text-slate-600 hover:text-slate-800 hover:bg-white/50'
                }`}
                style={billingPeriod === key ? {
                  background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)',
                  boxShadow: 'var(--shadow-3d-btn)',
                } : {}}
              >
                {label}
                {key !== 'monthly' && (
                  <span className={`ml-1.5 text-xs font-bold ${billingPeriod === key ? 'text-cyan-200' : 'text-emerald-600'}`}>SAVE</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Free tier card */}
        <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Icon3D icon={Lock} colour="blue" size="sm" />
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="font-bold text-slate-800 dark:text-white text-lg">Free</h3>
                {(!subscription || (subscription.subscription_status !== 'active' && subscription.subscription_status !== 'free_grant')) && (
                  <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-medium">Current</span>
                )}
              </div>
              <p className="text-sm text-slate-500">350 question bank · 40 questions per day — no card required</p>
            </div>
          </div>
          <div className="flex gap-4 text-sm text-slate-500 flex-wrap">
            <span className="flex items-center gap-1.5"><X className="w-4 h-4 text-slate-400" /> Diagnostics</span>
            <span className="flex items-center gap-1.5"><X className="w-4 h-4 text-slate-400" /> Analytics</span>
            <span className="flex items-center gap-1.5"><X className="w-4 h-4 text-slate-400" /> Exam Mode</span>
          </div>
        </div>

        {/* Tier cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-12 items-stretch">
          {TIERS.map((tier) => {
            const Icon = tier.icon;
            const colors = COLOR_MAP[tier.color];
            const price = tier.prices[billingPeriod];
            const saving = tier.savings[billingPeriod];
            const isCurrentPlan = (subscription?.subscription_status === 'active' || subscription?.subscription_status === 'free_grant') && currentPlanBase === tier.id;
            const isPopular = tier.popular;
            const isBestValue = tier.bestValue;

            return (
              <div
                key={tier.id}
                className={`bg-white rounded-2xl overflow-hidden transition-all hover:shadow-xl flex flex-col ${
                  isPopular
                    ? 'ring-2 ring-cyan-500 shadow-lg'
                    : isBestValue
                    ? 'ring-2 ring-cyan-500 shadow-lg'
                    : 'border border-slate-200 shadow-md'
                }`}
              >
                {/* Card header */}
                <div
                  className={`p-6 relative overflow-hidden ${isPopular ? 'text-white' : 'bg-slate-50'}`}
                  style={isPopular ? { background: 'linear-gradient(135deg, rgb(6,182,212) 0%, rgb(8,145,178) 100%)' } : {}}
                >
                  {/* Popular plan card — no shimmer on static cards */}

                  {/* Badge — always reserve this space so all 4 card headers are the same height */}
                  <div className="flex justify-center mb-3 min-h-[28px]">
                    {(isPopular || isBestValue) && (
                      <span
                        className="px-3 py-1 rounded-full text-xs font-bold text-white"
                        style={{
                          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                          boxShadow: '0 4px 12px rgba(6,182,212,0.4)'
                        }}
                      >
                        {isPopular ? 'MOST POPULAR' : 'BEST VALUE'}
                      </span>
                    )}
                  </div>

                  {/* Icon */}
                  <div className="flex justify-center mb-3">
                    <Icon3D icon={Icon} colour={tier.iconColour} size="md" />
                  </div>

                  {/* Name */}
                  <h3 className={`text-xl font-bold text-center mb-1 ${isPopular ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                    {tier.name}
                  </h3>

                  {/* Question bank label */}
                  <p className={`text-center text-xs font-medium mb-3 ${isPopular ? 'text-cyan-100' : 'text-slate-500 dark:text-slate-400'}`}>
                    {tier.questionBank}
                  </p>

                  {/* Price */}
                  <div className={`text-center ${isPopular ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                    <div className="flex items-end justify-center gap-1">
                      <span className="text-4xl font-bold">${price}</span>
                      <span className={`text-sm pb-1.5 ${isPopular ? 'text-cyan-100' : 'text-slate-500 dark:text-slate-400'}`}>
                        /{billingPeriod === 'monthly' ? 'mo' : billingPeriod === 'quarterly' ? 'qtr' : 'yr'}
                      </span>
                    </div>
                    {saving && billingPeriod !== 'monthly' && (
                      <span className={`text-xs font-semibold mt-1 inline-block px-2 py-0.5 rounded-full ${
                        isPopular ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {saving}
                      </span>
                    )}
                  </div>
                </div>

                {/* Features */}
                <div className="p-6 flex flex-col flex-1">
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        {f.included
                          ? <Icon3D icon={Check} colour="turquoise" size="sm" />
                          : <Icon3D icon={X} colour="purple" size="sm" />
                        }
                        <span className={`text-sm ${
                          f.included
                            ? f.highlight ? 'font-semibold text-cyan-700 dark:text-cyan-400' : 'text-slate-700 dark:text-slate-300'
                            : 'text-slate-400 dark:text-slate-500 line-through'
                        }`}>
                          {f.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA button */}
                  <button
                    onClick={() => handleSubscribe(tier.id)}
                    disabled={loading || isCurrentPlan}
                    className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 relative overflow-hidden ${
                      isCurrentPlan
                        ? 'bg-emerald-100 text-emerald-700 cursor-not-allowed'
                        : 'text-white hover:opacity-90'
                    }`}
                    style={!isCurrentPlan ? {
                      background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                      boxShadow: 'var(--shadow-3d-btn)'
                    } : {}}
                  >
                    {!isCurrentPlan && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />}
                    <span className="relative z-10 flex items-center gap-2">
                      {processingPlan === tier.id ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
                      ) : isCurrentPlan ? (
                        <><Check className="w-5 h-5" /> Current Plan</>
                      ) : (
                        <><CreditCard className="w-5 h-5" /> Subscribe</>
                      )}
                    </span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Comparison table */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden mb-10" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Feature Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold">
                  <th className="text-left py-3 sm:py-4 px-2 sm:px-6 text-xs sm:text-sm">Feature</th>
                  <th className="text-center py-3 sm:py-4 px-1 sm:px-3 text-xs sm:text-sm">Free</th>
                  <th className="text-center py-3 sm:py-4 px-1 sm:px-3 text-xs sm:text-sm">Explorer</th>
                  <th className="text-center py-3 sm:py-4 px-1 sm:px-3 text-xs sm:text-sm bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300">Essential</th>
                  <th className="text-center py-3 sm:py-4 px-1 sm:px-3 text-xs sm:text-sm">Pro</th>
                  <th className="text-center py-3 sm:py-4 px-1 sm:px-3 text-xs sm:text-sm">Elite</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Question Bank', '350', '1,250', '5,000', '15,000', '95,000+'],
                  ['Daily Questions', '40', '200', '500', '1,000', 'Unlimited'],
                  ['Diagnostic Tests', false, '1/day', 'Unlimited', 'Unlimited', 'Unlimited'],
                  ['Adaptive Learning', false, true, true, true, true],
                  ['Test Dates & Past Results', false, false, true, true, true],
                  ['Upload Own MCQs', false, false, true, true, true],
                  ['Create MCQs', false, false, true, true, true],
                  ['Exam Mode', false, true, true, true, true],
                  ['Full Analytics', false, true, true, true, true],
                  ['AI Study Plan', false, false, false, true, true],
                  ['AI Generation', false, false, false, true, true],
                  ['AI Scaffolding', false, false, false, false, true],
                  ['Priority Support', false, false, false, true, true],
                ].map(([label, ...vals], i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                    <td className="py-2.5 sm:py-3.5 px-2 sm:px-6 font-medium text-xs sm:text-sm text-slate-700 dark:text-slate-200">{label}</td>
                    {vals.map((v, j) => (
                      <td key={j} className={`text-center py-2.5 sm:py-3.5 px-1 sm:px-3 text-xs sm:text-sm ${j === 2 ? 'bg-cyan-50/50 dark:bg-cyan-900/20' : ''}`}>
                        {typeof v === 'boolean'
                          ? v
                            ? <Check className="w-4 h-4 text-emerald-500 mx-auto" />
                            : <X className="w-4 h-4 text-slate-300 dark:text-slate-600 mx-auto" />
                          : <span className={`font-medium ${j === 4 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-600 dark:text-slate-300'}`}>{v}</span>
                        }
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Security badge */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center gap-3 bg-slate-100 dark:bg-slate-800 px-6 py-3 rounded-xl">
            <Shield className="w-6 h-6 text-emerald-600" />
            <span className="text-slate-700 dark:text-slate-300 font-medium">Secure payment powered by Stripe</span>
          </div>
        </div>

        {/* Enterprise */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-center mb-8" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex justify-center mb-4">
            <Icon3D icon={Building2} colour="purple" size="md" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Enterprise &amp; Institutional Plans</h2>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            Medical schools, hospitals, and institutions can contact us for customised plans with team features, dedicated support, and volume pricing.
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="px-8 py-3 text-white rounded-xl hover:opacity-90 transition-colors font-semibold relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgb(6,182,212) 0%, rgb(8,145,178) 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
          >
            Contact for Enterprise Plan
          </button>
        </div>

        {/* Company info */}
        <div className="text-center text-slate-400 text-sm">
          <p className="font-semibold text-slate-600">ABUNDITA INVESTMENTS PTY LTD</p>
          <p>ABN: 55 100 379 299</p>
          <p>2/24 Edgar St, Coffs Harbour NSW 2450, Australia</p>
        </div>

      </div>
    </Layout>
  );
};

export default SubscriptionPage;
